import { useEffect, useMemo } from 'react'
import 'react-phone-number-input/style.css'
import PhoneInput from 'react-phone-number-input'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { AlertCircle, Phone, Mail, LifeBuoy, CheckCircle } from 'lucide-react'
import { LifelineType } from '@/constants'
import { useSessionStore } from '@/stores'

interface ContactSupportFormProps {
  lifelineType: number | null
  contacted?: boolean
  formErrors?: Record<string, string[]>
  formValues?: Record<string, any>
  phoneFallbackReason?: string | null
  onEmail?: (contactForm: Record<string, any>) => void
  /** Suppress the inline submit button — caller renders its own (e.g. BottomSheet footer). */
  hideSubmit?: boolean
  /** Fired whenever validity / submitting state changes — for external submit buttons. */
  onFormStateChange?: (state: { isValid: boolean; isSubmitting: boolean }) => void
  /** id assigned to the <form> so an external button can submit via the `form` attribute. */
  formId?: string
}

const isPhoneField = (field: any) => {
  const t = String(field?.type || '').toLowerCase()
  if (t === 'tel' || t === 'phone') {
    return true
  }
  const n = String(field?.name || '').toLowerCase()
  const l = String(field?.label || '').toLowerCase()
  return /phone|mobile|contact[\s_-]?number/.test(n) || /phone|mobile|contact\s*number/.test(l)
}

const PHONE_FORMAT = /^[\d\s\-+().]+$/
const PHONE_MIN_DIGITS = 7

const buildSchema = (fields: any[]) => {
  const shape: Record<string, z.ZodTypeAny> = {}
  fields.forEach(field => {
    let base: z.ZodTypeAny = z.string()
    if (field.required) {
      base = (base as z.ZodString).min(1, `${field.label || field.name} is required`)
    } else {
      base = base.optional()
    }
    if (field.type === 'email') {
      base = field.required
        ? (z.string().min(1, `${field.label || 'Email'} is required`) as z.ZodString).email(
            'Please enter a valid email'
          )
        : z.string().email('Please enter a valid email').optional().or(z.literal(''))
    } else if (isPhoneField(field)) {
      const phone = z
        .string()
        .regex(PHONE_FORMAT, 'Please enter a valid phone number')
        .refine(v => v.replace(/\D/g, '').length >= PHONE_MIN_DIGITS, 'Please enter a valid phone number')
      base = field.required
        ? z
            .string()
            .min(1, `${field.label || 'Phone'} is required`)
            .pipe(phone)
        : z.union([z.literal(''), phone]).optional()
    }
    shape[field.name] = base
  })
  return z.object(shape)
}

const ContactSupportForm = ({
  lifelineType,
  contacted = false,
  formErrors = {},
  formValues = {},
  phoneFallbackReason = null,
  onEmail,
  hideSubmit = false,
  onFormStateChange,
  formId,
}: ContactSupportFormProps) => {
  const session = useSessionStore(state => state.session) as any
  const campaign = useSessionStore(state => state.campaign)

  const isLifelinePhone = lifelineType === LifelineType.Phone
  const isLifelineEmail = lifelineType === LifelineType.Email

  const supportPhone = useMemo(() => {
    if (session?.phone_contact_url) {
      const pos = session.phone_contact_url[4] === '+' ? 5 : 4
      return session.phone_contact_url.slice(pos)
    }
    return ''
  }, [session?.phone_contact_url])

  const customerCountry = (session?.customer as any)?.country || 'US'

  const emailFields: any[] = useMemo(() => {
    // Merchant configs repeat the name/contact block with differing `name` keys but the
    // same label, so a duplicate on either identifier drops the field.
    const seen = new Set<string>()
    const norm = (v: any) =>
      String(v ?? '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
    return ((campaign as any)?.support_email_fields || [])
      .filter((f: any) => f !== null)
      .filter((f: any) => {
        const keys = [norm(f.name), norm(f.label)].filter(Boolean)
        if (!keys.length || keys.some(k => seen.has(k))) {
          return false
        }
        keys.forEach(k => seen.add(k))
        return true
      })
  }, [campaign])

  const defaultValues = useMemo(() => {
    const defaults: Record<string, any> = {}
    const customer = session?.customer
    emailFields.forEach((field: any) => {
      let value: any = null
      if (customer) {
        if (field.name === 'email') {
          value = customer.email
        } else if (field.name === 'phone') {
          value = customer.phone
        } else if (field.name === 'first_name') {
          value = customer.first_name
        } else if (field.name === 'last_name') {
          value = customer.last_name
        } else if (field.name === 'name') {
          value = `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
        }
      }
      if (value == null && formValues?.[field.name] != null) {
        value = formValues[field.name]
      }
      defaults[field.name] = value ?? ''
    })
    return defaults
  }, [emailFields, session?.customer, formValues])

  const schema = useMemo(() => buildSchema(emailFields), [emailFields])
  type FormData = Record<string, any>

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues,
  })

  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  const getFieldType = (field: { type?: string }): string => {
    if (!field.type || field.type === 'text') {
      return 'text'
    }
    if (field.type === 'email') {
      return 'email'
    }
    if (field.type === 'tel' || field.type === 'phone') {
      return 'tel'
    }
    if (field.type === 'number') {
      return 'number'
    }
    return 'text'
  }

  const onSubmit = (data: FormData) => {
    onEmail?.(data)
  }

  useEffect(() => {
    onFormStateChange?.({ isValid, isSubmitting })
  }, [isValid, isSubmitting, onFormStateChange])

  if (contacted) {
    return (
      <>
        <div className="lifeline-header-icon">
          <CheckCircle size={36} strokeWidth={1.75} />
        </div>
        <div className="step-intro">
          <h1 className="lifeline-heading">Message sent</h1>
          <hr />
        </div>
        <p className="lifeline-subtitle">
          Your query has been submitted. For a faster response you can also reach us directly.
        </p>
        <div className="lifeline-contacted-actions">
          {session?.phone_contact_url && (
            <a href={session.phone_contact_url} className="lifeline-contacted-link">
              <Phone size={20} /> {supportPhone}
            </a>
          )}
          {session?.support_email && (
            <a href={`mailto:${session.support_email}`} className="lifeline-contacted-link">
              <Mail size={20} /> {session.support_email}
            </a>
          )}
        </div>
      </>
    )
  }

  if (isLifelinePhone) {
    return (
      <>
        {phoneFallbackReason && (
          <div className="lifeline-fallback-notice">
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            <span>{phoneFallbackReason}</span>
          </div>
        )}
        <div className="lifeline-header-icon">
          <Phone size={36} strokeWidth={1.75} />
        </div>
        <div className="step-intro">
          <h1 className="lifeline-heading">Give us a call</h1>
          <hr />
        </div>
        <p className="lifeline-subtitle">We have a specialist on standby to help you right now.</p>
        <a href={session?.phone_contact_url || '#'} className="btn-base lifeline-channel-cta">
          <Phone size={20} /> {supportPhone}
        </a>
      </>
    )
  }

  if (isLifelineEmail) {
    const renderField = (field: any, index: number) => (
      <div key={`input-data-field-${index}-${field.name}`} className="form-group">
        <label htmlFor={field.name} className="block text-base font-medium mb-1" style={{ color: 'var(--text-color)' }}>
          {field.label}
          {field.required && <span className="lifeline-required-mark">*</span>}
        </label>

        {field.type === 'select' ? (
          <select id={field.name} className="form-select" {...register(field.name)}>
            <option value="">-- Select --</option>
            {(field.values || []).map((opt: any) => (
              <option key={opt.value || opt} value={opt.value || opt}>
                {opt.text || opt}
              </option>
            ))}
          </select>
        ) : field.type === 'textarea' ? (
          <textarea id={field.name} className="form-control" rows={5} {...register(field.name)} />
        ) : isPhoneField(field) ? (
          <Controller
            control={control}
            name={field.name as any}
            render={({ field: ctl }) => (
              <PhoneInput
                international
                defaultCountry={(customerCountry as any) || 'US'}
                value={ctl.value || ''}
                onChange={v => ctl.onChange(v || '')}
                className="omp-phone-input"
              />
            )}
          />
        ) : (
          <input id={field.name} type={getFieldType(field)} className="form-control" {...register(field.name)} />
        )}

        {errors[field.name] && (
          <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
            <AlertCircle size={14} />
            {String(errors[field.name]?.message || '')}
          </p>
        )}
        {formErrors[field.name] && !errors[field.name] && (
          <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
            <AlertCircle size={14} />
            {formErrors[field.name][0]}
          </p>
        )}
      </div>
    )

    const isFirstName = (f: any) => {
      if (!f) {
        return false
      }
      const n = String(f.name || '').toLowerCase()
      const l = String(f.label || '').toLowerCase()
      return /(^|[_\-\s])first/.test(n) || /^first[\s_-]?name$/.test(l)
    }
    const isLastName = (f: any) => {
      if (!f) {
        return false
      }
      const n = String(f.name || '').toLowerCase()
      const l = String(f.label || '').toLowerCase()
      return /(^|[_\-\s])last/.test(n) || /^last[\s_-]?name$/.test(l)
    }

    const renderedNodes: React.ReactNode[] = []
    for (let i = 0; i < emailFields.length; i++) {
      const field = emailFields[i]
      const next = emailFields[i + 1]
      if (isFirstName(field) && isLastName(next)) {
        renderedNodes.push(
          <div key={`pair-${i}`} className="lifeline-form-row">
            {renderField(field, i)}
            {renderField(next, i + 1)}
          </div>
        )
        i++
      } else {
        renderedNodes.push(renderField(field, i))
      }
    }

    return (
      <>
        <div className="lifeline-header-icon">
          <LifeBuoy size={36} strokeWidth={1.75} />
        </div>
        <div className="step-intro">
          <h1 className="lifeline-heading">Need a hand?</h1>
          <hr />
        </div>
        <p className="lifeline-subtitle">Fill out the information below and a specialist will contact you shortly.</p>

        <form id={formId} className="lifeline-form" onSubmit={handleSubmit(onSubmit)}>
          {emailFields.length > 0 && <fieldset>{renderedNodes}</fieldset>}

          {!hideSubmit && (
            <div className="flex justify-center mt-3 mb-3 w-full">
              <button
                type="submit"
                className="btn-base"
                disabled={!isValid || isSubmitting}
                style={{
                  backgroundColor: 'var(--theme-color)',
                  color: 'var(--button-text-color, #fff)',
                  borderColor: 'var(--theme-color)',
                }}>
                Send Message
              </button>
            </div>
          )}
        </form>
      </>
    )
  }

  return null
}

export default ContactSupportForm
